"use client";

import * as React from "react";
import SelectBase, {
  type GroupBase,
  type Props as RSProps,
} from "react-select";
import VirtualizedSelectBase from "react-select-virtualized";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

export type Option = { label: string; value: string };
export type GroupOption = { label: string; options: Option[] };

export type SelectOption = Option | GroupOption;

export type SelectBlockProps = {
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  error?: React.ReactNode;
  /** Selected value(s). Matches react-select value types based on multiple */
  value?: Option | Option[] | null;
  /** Change handler passthrough */
  onChange?: RSProps<Option, boolean, GroupBase<Option>>["onChange"];
  /** Options list; supports grouped options when using { label, options } */
  options: ReadonlyArray<SelectOption>;
  /** Enable multiple selection */
  multiple?: boolean;
  /** If true, uses react-select-virtualized */
  virtualised?: boolean;
  /** Element to portal the menu into; pass a DOM element or undefined for inline */
  portaling?: HTMLElement | null | undefined;
  /** Disable control */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Additional class for the outer root */
  className?: string;
  /** Required flag (adds aria-invalid when error present) */
  required?: boolean;
};

function mapGroupedOptions(
  options: ReadonlyArray<SelectOption>
): ReadonlyArray<Option> | ReadonlyArray<GroupBase<Option>> {
  // If any item looks like a group, return as GroupBase; else return options as Option[]
  const anyGroup = options.some(
    (it) =>
      (it as GroupOption).options && Array.isArray((it as GroupOption).options)
  );
  if (anyGroup) {
    return options.map((it) => {
      const maybeGroup = it as GroupOption;
      if (maybeGroup.options && Array.isArray(maybeGroup.options)) {
        return {
          label: maybeGroup.label,
          options: maybeGroup.options as Option[],
        };
      }
      const single = it as Option;
      return { label: "", options: [single] };
    }) as ReadonlyArray<GroupBase<Option>>;
  }
  return options as ReadonlyArray<Option>;
}

const ControlStyles: RSProps<Option, boolean>["styles"] = {
  control: (base) => ({
    ...base,
    border: undefined,
    boxShadow: "none",
    backgroundColor: "transparent",
    borderRadius: "10px",
    padding: "0 12px",
  }),
  multiValue: (base) => ({ ...base, borderRadius: "8px" }),
  valueContainer: (base) => ({ ...base, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, padding: 0 }),
  placeholder: (base) => ({ ...base }),
  menuPortal: (base) => ({ ...base, zIndex: 60, border: "0" }),
  // Override menu background to use CSS variable for dark mode support
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--popover)",
    color: "var(--popover-foreground)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--primary)"
      : state.isFocused
        ? "var(--accent)"
        : "transparent",
    color: state.isSelected
      ? "var(--primary-foreground)"
      : "var(--popover-foreground)",
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--foreground)",
  }),
};

export const SelectBlock = React.forwardRef<HTMLDivElement, SelectBlockProps>(
  (
    {
      label,
      helpText,
      error,
      value,
      onChange,
      options,
      multiple,
      virtualised,
      portaling,
      disabled,
      placeholder,
      className,
      required,
    },
    ref
  ) => {
    const isMulti = !!multiple;
    const instanceId = React.useId();
    const asGroups = React.useMemo(
      () => mapGroupedOptions(options),
      [options]
    ) as any;

    const SelectComponent: any = virtualised
      ? VirtualizedSelectBase
      : SelectBase;

    const ariaInvalid = !!error || undefined;

    // Map classes via className props that react-select exposes
    const classNames: RSProps<Option, boolean>["classNames"] = {
      control: () =>
        cn(styleDrivers.select.control, ariaInvalid && "aria-invalid"),
      valueContainer: () => cn(styleDrivers.select.valueContainer),
      placeholder: () => cn(styleDrivers.select.placeholder),
      singleValue: () => cn(styleDrivers.select.singleValue),
      multiValue: () => cn(styleDrivers.select.multiValue),
      multiValueLabel: () => cn(styleDrivers.select.multiValueLabel),
      multiValueRemove: () => cn(styleDrivers.select.multiValueRemove),
      indicatorsContainer: () => cn(styleDrivers.select.indicators),
      menu: () => cn(styleDrivers.select.menu),
      option: (state: any) =>
        cn(
          styleDrivers.select.option,
          state.isFocused && styleDrivers.select.optionFocused,
          state.isSelected && styleDrivers.select.optionSelected
        ),
    } as any;

    return (
      <div className={cn(styleDrivers.select.root, className)} ref={ref}>
        {label && (
          <label className={cn(styleDrivers.select.label)}>{label}</label>
        )}
        <SelectComponent
          classNamePrefix="rs"
          instanceId={instanceId}
          isMulti={isMulti}
          isDisabled={disabled}
          value={value as any}
          onChange={onChange as any}
          options={asGroups}
          menuPortalTarget={
            portaling ?? (typeof document !== "undefined" ? document.body : undefined)
          }
          placeholder={placeholder}
          aria-invalid={ariaInvalid as any}
          styles={ControlStyles}
          classNames={classNames as any}
        />
        {helpText && !error && (
          <div className={cn(styleDrivers.select.helpText, "text-caption")}>
            {helpText}
          </div>
        )}
        {error && (
          <div className={cn(styleDrivers.select.errorText)}>{error}</div>
        )}
      </div>
    );
  }
);

SelectBlock.displayName = "SelectBlock";

export default SelectBlock;
